import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Settings } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import { useSyncOuEnLigne } from '../../context/SyncContext';
import { transportStatementsAPI, urlImpressionEtat } from '../../lib/inboundRentalsApi';
import { messageErreur } from '../../lib/erreurs';
import { formatDate } from '../../lib/formats';
import { cn, formatCurrency } from '../../lib/utils';
import { usePagination } from '../../hooks/usePagination';
import { useRafraichirSur } from '../../hooks/useRafraichirSur';
import { PrintMenu } from '../business/PrintMenu';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { EmptyState } from '../ui/empty-state';
import { LoadingState } from '../ui/loading-state';
import { BarrePagination } from '../ui/pagination-bar';
import { StatutBadge } from '../ui/StatutBadge';
import { toast } from '../ui/sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import ReglagesEtatsDialog from './ReglagesEtatsDialog';

/** « BPS F005-PKS »: la référence que prendra le prochain état validé (même format que le serveur). */
export const prochaineReference = (liste) => {
  const sigleTransporteur = liste?.numbering?.statement_code;
  const sigleSociete = liste?.company_code;
  const numero = liste?.numbering?.next_number;
  if (!sigleTransporteur || !sigleSociete || !numero) return null;
  return `${sigleTransporteur} F${String(numero).padStart(3, '0')}-${sigleSociete}`;
};

const Chiffre = ({ valeur, className }) => (
  <TableCell className={cn('whitespace-nowrap text-right tabular-nums', className)}>
    {valeur === null || valeur === undefined ? '—' : formatCurrency(valeur)}
  </TableCell>
);

/**
 * Onglet « États de paiement » d'un contrat réglé à l'état: la liste des états
 * du transporteur (le plus récent d'abord), le bouton « Nouvel état » et, pour
 * l'administrateur, les réglages de numérotation. Direction seulement.
 */
const TransportStatementsPanel = ({ contract, onChanged }) => {
  const navigate = useNavigate();
  const { isSuperAdmin, isAdminSociete } = useAuth();
  const estAdmin = Boolean(isSuperAdmin || isAdminSociete);
  const enLigne = useSyncOuEnLigne().isOnline !== false;

  const [liste, setListe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [reglagesOuverts, setReglagesOuverts] = useState(false);

  const charger = useCallback(
    async ({ silencieux = false } = {}) => {
      if (!silencieux) setLoading(true);
      setFailed(false);
      try {
        setListe(await transportStatementsAPI.list(contract.id));
      } catch (error) {
        setFailed(true);
        toast.error(messageErreur(error, 'Impossible de charger les états de paiement'));
      } finally {
        setLoading(false);
      }
    },
    [contract.id],
  );

  useEffect(() => {
    charger();
  }, [charger]);

  useRafraichirSur(['locations', 'finance', 'trajets', 'depenses'], charger);

  const etats = useMemo(() => liste?.items || [], [liste]);
  const pagination = usePagination(etats, { cle: 'etats' });

  const ouvrir = (etat) => navigate(`/rentals/inbound/${contract.id}/etats/${etat.id}`);
  const nouveau = () => navigate(`/rentals/inbound/${contract.id}/etats/nouveau`);

  const reference = prochaineReference(liste);
  const sigleTransporteurManquant = liste && !liste.numbering?.statement_code;
  const sigleSocieteManquant = liste && !liste.company_code;

  if (loading && !liste) return <LoadingState shape="table" />;
  if (failed && !liste) {
    return (
      <EmptyState
        variant="error"
        title="Les états de paiement n’ont pas pu être chargés"
        description="Réessayez dans un instant."
        actionLabel="Réessayer"
        onAction={charger}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="transport-statements-panel">
      <div className="flex flex-wrap items-center gap-2">
        {reference ? (
          <Badge variant="muted" data-testid="prochaine-reference">
            Prochain état : {reference}
          </Badge>
        ) : null}
        {sigleTransporteurManquant ? (
          <Badge variant="warning" data-testid="sigle-transporteur-manquant">
            Sigle du transporteur à renseigner
          </Badge>
        ) : null}
        {sigleSocieteManquant ? (
          <Badge variant="warning" data-testid="sigle-societe-manquant">
            Sigle de la société à renseigner
          </Badge>
        ) : null}
        <div className="ml-auto flex items-center gap-2">
          {estAdmin ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setReglagesOuverts(true)}
              data-testid="reglages-etats-btn"
            >
              <Settings className="h-4 w-4" aria-hidden="true" />
              Réglages
            </Button>
          ) : null}
          <Button size="sm" onClick={nouveau} disabled={!enLigne} data-testid="nouvel-etat-btn">
            <Plus className="h-4 w-4" aria-hidden="true" />
            Nouvel état
          </Button>
        </div>
      </div>

      {etats.length === 0 ? (
        <EmptyState
          illustration="donnee"
          title="Aucun état de paiement"
          description="Établissez le premier état avec des trajets dépotés."
          actionLabel="Nouvel état"
          onAction={nouveau}
        />
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-border/70">
            <Table data-testid="liste-etats-paiement" stickyFirstColumn={false}>
              <TableHeader>
                <TableRow>
                  <TableHead>Référence</TableHead>
                  <TableHead>Établie le</TableHead>
                  <TableHead className="text-right">Trajets</TableHead>
                  <TableHead className="text-right">Montant</TableHead>
                  <TableHead className="text-right">Retenues</TableHead>
                  <TableHead className="text-right">Solde</TableHead>
                  <TableHead className="text-right">Net à payer</TableHead>
                  <TableHead>État</TableHead>
                  <TableHead>Paiement</TableHead>
                  <TableHead className="w-px" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagination.lignes.map((etat) => (
                  <TableRow
                    key={etat.id}
                    className={cn('cursor-pointer', etat.status === 'ANNULE' && 'opacity-60')}
                    onClick={() => ouvrir(etat)}
                    data-testid={`etat-${etat.id}`}
                  >
                    <TableCell className="whitespace-nowrap font-semibold">
                      <button
                        type="button"
                        className="text-left underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        onClick={(event) => {
                          event.stopPropagation();
                          ouvrir(etat);
                        }}
                      >
                        {etat.reference || 'Brouillon'}
                      </button>
                    </TableCell>
                    <TableCell className="whitespace-nowrap tabular-nums">{formatDate(etat.issued_at)}</TableCell>
                    <TableCell className="whitespace-nowrap text-right tabular-nums">{etat.lines_count}</TableCell>
                    <Chiffre valeur={etat.total_amount} />
                    <Chiffre valeur={etat.total_retention} />
                    <Chiffre valeur={etat.total_balance} />
                    <Chiffre valeur={etat.net_amount} className="font-semibold" />
                    <TableCell>
                      <StatutBadge domaine="statut_etat_paiement" code={etat.status} />
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {etat.payment_status ? (
                        <div className="flex flex-col items-start gap-0.5">
                          <StatutBadge domaine="statut_paiement_etat" code={etat.payment_status} />
                          {etat.payment_status !== 'PAYE' && etat.bill_remaining ? (
                            <span className="text-xs tabular-nums text-muted-foreground">
                              Reste {formatCurrency(etat.bill_remaining)}
                            </span>
                          ) : null}
                        </div>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell onClick={(event) => event.stopPropagation()}>
                      {etat.status === 'VALIDE' ? (
                        <PrintMenu
                          label="Imprimer"
                          items={[
                            {
                              label: etat.reference || 'État de paiement',
                              url: urlImpressionEtat(contract.id, etat.id),
                              filename: `etat-paiement-${etat.reference || etat.id}`,
                              formats: ['pdf', 'xlsx'],
                            },
                          ]}
                        />
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <BarrePagination pagination={pagination} nom="états de paiement" />
        </>
      )}

      {estAdmin ? (
        <ReglagesEtatsDialog
          open={reglagesOuverts}
          onOpenChange={setReglagesOuverts}
          onSaved={async () => {
            await charger({ silencieux: true });
            if (onChanged) await onChanged();
          }}
        />
      ) : null}
    </div>
  );
};

export default TransportStatementsPanel;
